import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Checkbox, List, Spin, Typography, notification } from "antd";
import { FilePdfOutlined } from "@ant-design/icons";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as termsAction from "actions/terms-action";
import { themeColors } from "theme";

const { Text, Title } = Typography;

const mapDispatchToProps = (dispatch) =>
    bindActionCreators({ ...termsAction }, dispatch);

const TermsAcceptanceModal = ({
    visible,
    onAccepted,
    onLogout,
    getPendingTermsAction,
    getTermPreviewAction,
    acceptTermsAction,
}) => {
    const [loading, setLoading] = useState(false);
    const [accepting, setAccepting] = useState(false);
    const [pendingTerms, setPendingTerms] = useState([]);
    const [checked, setChecked] = useState(false);
    const [previewLoading, setPreviewLoading] = useState({});

    const uniqueTerms = useMemo(() => {
        const seen = new Set();
        return pendingTerms.filter((term) => {
            const key = term?.term_version_id;
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [pendingTerms]);

    useEffect(() => {
        if (!visible) return;
        const load = async () => {
            setLoading(true);
            try {
                const res = await getPendingTermsAction();
                setPendingTerms(res?.pendingTerms ?? []);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [visible]);

    const handlePreview = async (termVersionId) => {
        setPreviewLoading((prev) => ({ ...prev, [termVersionId]: true }));
        try {
            const termVersion = await getTermPreviewAction(termVersionId);
            const url = termVersion?.download_url || termVersion?.downloadUrl;
            if (!url) {
                notification.error({ message: "URL do termo indisponível no momento." });
                return;
            }
            window.open(url, "_blank", "noopener,noreferrer");
        } finally {
            setPreviewLoading((prev) => ({ ...prev, [termVersionId]: false }));
        }
    };

    const handleAccept = async () => {
        setAccepting(true);
        try {
            const res = await acceptTermsAction("backoffice");
            if (res?.success === false) return;
            notification.success({
                message: "Aceite registrado",
                description: "Seu aceite dos termos foi registrado e está sendo processado.",
            });
            onAccepted?.();
        } finally {
            setAccepting(false);
        }
    };

    return (
        <Modal
            open={visible}
            footer={null}
            width={720}
            closable={false}
            maskClosable={false}
            keyboard={false}
            destroyOnClose
            title={
                <div>
                    <Title level={4} style={{ margin: 0 }}>Aceite dos Termos de Uso</Title>
                    <Text style={{ fontSize: 14, fontWeight: "normal" }}>
                        Para continuar utilizando o backoffice, é necessário ler e aceitar os termos abaixo.
                    </Text>
                </div>
            }
        >
            {loading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
                    <Spin />
                </div>
            ) : (
                <>
                    {uniqueTerms.length === 0 ? (
                        <Text>Nenhum termo pendente encontrado. Clique em Continuar.</Text>
                    ) : (
                        <List
                            bordered
                            dataSource={uniqueTerms}
                            renderItem={(term) => (
                                <List.Item
                                    actions={[
                                        <Button
                                            key="preview"
                                            type="link"
                                            icon={<FilePdfOutlined />}
                                            loading={!!previewLoading[term.term_version_id]}
                                            onClick={() => handlePreview(term.term_version_id)}
                                        >
                                            Visualizar PDF
                                        </Button>,
                                    ]}
                                >
                                    <List.Item.Meta
                                        title={term.term_name || term.name || "Termo de uso"}
                                        description={term.description || term.subject_document}
                                    />
                                </List.Item>
                            )}
                        />
                    )}

                    <div style={{ marginTop: 20 }}>
                        <Checkbox
                            checked={checked}
                            onChange={(e) => setChecked(e.target.checked)}
                        >
                            Li e aceito os termos de uso
                        </Checkbox>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: 24,
                            gap: 12,
                        }}
                    >
                        <Button onClick={onLogout} disabled={accepting}>
                            Sair
                        </Button>
                        <Button
                            type="primary"
                            style={{ backgroundColor: themeColors.darkColor, borderColor: themeColors.darkColor }}
                            onClick={handleAccept}
                            loading={accepting}
                            disabled={!checked || uniqueTerms.length === 0}
                        >
                            Aceitar e continuar
                        </Button>
                    </div>
                </>
            )}
        </Modal>
    );
};

export default connect(null, mapDispatchToProps)(TermsAcceptanceModal);
